import React, { useState } from 'react';
import { KeyGoal } from '../../types';
import { NotesModal } from '../../components/applications/NotesModal';
import { formatMonthYear } from '../../utils/date-formatter';

interface ProjectItem {
    project_id?: string;
    name: string;
    overview_and_goals: string;
    presidio_controlled?: string;
    estimated_completion?: string;
    it_initiative_alignment?: string;
    modernization_plan_project?: string;
    start_month?: string;
    end_month?: string;
    dependency_project_id?: string;
    goals?: KeyGoal[];
    presidio_areas?: string[];
}

interface ProjectsTabProps {
    projects?: ProjectItem[];
    onEditProject?: (project: ProjectItem) => void;
}

const ProjectsTab: React.FC<ProjectsTabProps> = ({ projects = [], onEditProject }) => {
    const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
    const [currentNotes, setCurrentNotes] = useState<string | null>(null);
    const [modalTitle, setModalTitle] = useState('');

    return (
        <div className="px-4 py-4">
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_18px_40px_rgba(15,42,79,0.08)]">
                <div className="border-b border-slate-200 bg-gradient-to-r from-slate-50 via-white to-sky-50 px-5 py-4">
                    <div className="flex items-center gap-3">
                        <span className="inline-flex items-center rounded-full bg-[#0F2A4F] px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-white">
                            Projects
                        </span>
                        <h1 className="text-2xl font-bold text-[#0F2A4F]">Projects</h1>
                    </div>
                    <p className="mt-1.5 max-w-3xl text-sm text-slate-600">
                        A portfolio view of active and proposed projects, including timing, dependencies, governance ownership, and modernization status.
                    </p>
                </div>

                <div className="p-5">
                    {projects.length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-center text-slate-500">
                            No projects found.
                        </div>
                    ) : (
                        <div className="overflow-x-auto rounded-2xl border border-slate-200">
                            <table className="min-w-full border-collapse bg-white">
                            <thead>
                                <tr className="bg-slate-50">
                                    <th className="border-b border-slate-200 px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 min-w-[180px]">Project Name</th>
                                    <th className="border-b border-slate-200 px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 min-w-[340px]">Overview and Goals</th>
                                    <th className="border-b border-slate-200 px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 min-w-[120px]">Start</th>
                                    <th className="border-b border-slate-200 px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 min-w-[120px]">End</th>
                                    <th className="border-b border-slate-200 px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 min-w-[150px]">Presidio Controlled</th>
                                    <th className="border-b border-slate-200 px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 min-w-[160px]">Modernization</th>
                                    <th className="border-b border-slate-200 px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 min-w-[180px]">Dependency</th>
                                </tr>
                            </thead>
                            <tbody className="text-sm">
                            {projects.map((project, index) => (
                                <tr
                                    key={index}
                                    className="border-b border-slate-200 text-sm transition-colors hover:bg-slate-50/80"
                                >
                                    <td className="px-4 py-4 text-slate-900 font-semibold min-w-[180px] align-top">
                                        <div className="max-w-[220px] leading-5">{project.name}</div>
                                    </td>
                                    <td className="px-4 py-4 text-slate-700 min-w-[340px] align-top">
                                        <div 
                                            className="line-clamp-3 cursor-pointer rounded-lg bg-slate-50 px-3 py-2 text-sm leading-6 transition-colors hover:bg-sky-50 hover:text-sky-700"
                                            onClick={() => {
                                                setCurrentNotes(project.overview_and_goals);
                                                setModalTitle('Project Overview and Goals');
                                                setIsNotesModalOpen(true);
                                            }}
                                        >
                                            {project.overview_and_goals}
                                        </div>
                                    </td>
                                    <td className="px-4 py-4 text-slate-700 font-medium min-w-[120px] align-top">
                                        {formatMonthYear(project.start_month)}
                                    </td>
                                    <td className="px-4 py-4 text-slate-700 font-medium min-w-[120px] align-top">
                                        {formatMonthYear(project.end_month)}
                                    </td>
                                    <td className="px-4 py-4 text-slate-700 font-medium min-w-[150px] align-top">
                                        <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ring-1 ${
                                            project.presidio_controlled === 'Yes'
                                                ? 'bg-[#0F2A4F]/8 text-[#0F2A4F] ring-[#0F2A4F]/10'
                                                : 'bg-slate-100 text-slate-600 ring-slate-200'
                                        }`}>
                                            {project.presidio_controlled || 'No'}
                                        </span>
                                    </td>
                                    <td className="px-4 py-4 text-slate-700 font-medium min-w-[160px] align-top">
                                        <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ring-1 ${
                                            project.modernization_plan_project === 'Yes' 
                                                ? 'bg-emerald-100 text-emerald-800 ring-emerald-200' 
                                                : 'bg-slate-100 text-slate-700 ring-slate-200'
                                        }`}>
                                            {project.modernization_plan_project || 'No'}
                                        </span>
                                    </td>
                                    <td className="px-4 py-4 text-slate-700 font-medium min-w-[180px] align-top">
                                        <div className="max-w-[220px] leading-5">
                                            {project.dependency_project_id ? 
                                                projects.find(p => p.project_id === project.dependency_project_id)?.name || project.dependency_project_id : 
                                                '--'}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
            
            <NotesModal
                isOpen={isNotesModalOpen}
                onClose={() => setIsNotesModalOpen(false)}
                notes={currentNotes}
                title={modalTitle}
            />
        </div>
    );
};

export default ProjectsTab;
